import React, { useState } from "react";
import { Group, NumberInput, Select, Switch, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { SettingsField } from "../common";
import StudioTabShell from "./StudioTabShell";
import {
  SLOT_INTERVAL_OPTIONS,
  SLOT_INTERVAL_TOOLTIP,
} from "../../../utils/formConstants";
import {
  AdvancedSettingsValues,
  buildAdvancedSettingsPayload,
  formatBookingHorizon,
  formatSlotInterval,
  normalizeAdvancedSettings,
  validateAdvancedSettings,
} from "../../../utils/studioSettings";
import {
  BADGE_DISPLAY_FIELDS,
  BADGE_DISPLAY_MODE_OPTIONS,
  BADGE_SETTING_FIELDS,
  badgeDisplayModeLabel,
  badgeSettingsFrom,
} from "../../../utils/badgeSettings";

export interface AdvancedSettingsTabProps {
  businessInfo: any;
  editing: boolean;
  saving?: boolean;
  readOnly?: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (payload: Record<string, any>) => void | Promise<void>;
}

const yesNo = (v?: boolean) => (v ? "Yes" : "No");

export default function AdvancedSettingsTab({
  businessInfo,
  editing,
  saving = false,
  readOnly = false,
  onEdit,
  onCancel,
  onSave,
}: AdvancedSettingsTabProps) {
  const [values, setValues] = useState<AdvancedSettingsValues>(() =>
    normalizeAdvancedSettings(businessInfo),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isLargeScreen = useMediaQuery("(min-width: 769px)");

  // On larger screens the edit column is wide; center the switch within it (in
  // edit mode only) so it isn't marooned at the far right. Read mode and mobile
  // keep the default alignment.
  const centerSwitch = (node: React.ReactNode) =>
    isLargeScreen ? (
      <Group justify="center" w="100%">
        {node}
      </Group>
    ) : (
      node
    );

  const startEdit = () => {
    setValues(normalizeAdvancedSettings(businessInfo));
    setErrors({});
    onEdit();
  };

  const setField = (field: keyof AdvancedSettingsValues, value: any) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));
  };

  const handleSave = () => {
    const validationErrors = validateAdvancedSettings(values);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    // Include the salon id — PUT /business-locations keys the update off it — and only this
    // view's fields.
    onSave({ id: businessInfo?.id, ...buildAdvancedSettingsPayload(values) });
  };

  return (
    <StudioTabShell
      editing={editing}
      readOnly={readOnly}
      saving={saving}
      onEdit={startEdit}
      onCancel={onCancel}
      onSave={handleSave}
    >
      <SettingsField
        label="Booking Delay"
        info="Sets minimum buffer time between now and when a customer can book their next appointment."
        editing={editing}
        editControl={
          <NumberInput
            aria-label="Booking Delay (minutes)"
            value={values.booking_delay_mins}
            onChange={(v) => setField("booking_delay_mins", v)}
            min={0}
            max={2880}
            step={5}
            suffix=" minutes"
            w="100%"
          />
        }
      >
        {businessInfo?.booking_delay_mins ?? 60} minutes
      </SettingsField>

      <SettingsField
        label="Availability Slot Interval"
        info={SLOT_INTERVAL_TOOLTIP}
        editing={editing}
        editControl={
          <Select
            aria-label="Availability Slot Interval"
            data={SLOT_INTERVAL_OPTIONS}
            value={String(values.availability_slot_interval_mins)}
            onChange={(v) =>
              setField(
                "availability_slot_interval_mins",
                v ? parseInt(v, 10) : 15,
              )
            }
            allowDeselect={false}
            w="100%"
          />
        }
      >
        {formatSlotInterval(businessInfo?.availability_slot_interval_mins)}
      </SettingsField>

      <SettingsField
        label="Customer Booking Horizon"
        info="Sets how far in advance customers can book appointments."
        editing={editing}
        editControl={
          <NumberInput
            aria-label="Customer Booking Horizon (days)"
            value={values.customer_booking_horizon_days ?? ""}
            onChange={(v) =>
              setField(
                "customer_booking_horizon_days",
                v === "" || v === undefined ? null : v,
              )
            }
            placeholder="Unlimited"
            clampBehavior="none"
            min={0}
            max={365}
            step={1}
            error={errors.customer_booking_horizon_days}
            description={
              values.customer_booking_horizon_days === 0 ? (
                <Text component="span" c="red" size="xs">
                  Customers can only book same-day; leave blank for unlimited.
                </Text>
              ) : undefined
            }
            w="100%"
          />
        }
      >
        {formatBookingHorizon(businessInfo?.customer_booking_horizon_days)}
      </SettingsField>

      <SettingsField
        label="Appointments Require Staff Approval"
        info="Requires staff approval for new self-service bookings before blocking the time in your calendar."
        editing={editing}
        editControl={centerSwitch(
          <Switch
            aria-label="Appointments Require Staff Approval"
            checked={values.appts_need_accepting}
            onChange={(e) =>
              setField("appts_need_accepting", e.currentTarget.checked)
            }
          />,
        )}
      >
        {yesNo(businessInfo?.appts_need_accepting)}
      </SettingsField>

      <SettingsField
        label="Request Customer Appointment Confirmation"
        info="Sends confirmation requests to customers a few days before their scheduled appointment."
        editing={editing}
        editControl={centerSwitch(
          <Switch
            aria-label="Request Customer Appointment Confirmation"
            checked={values.appts_need_confirmation}
            onChange={(e) =>
              setField("appts_need_confirmation", e.currentTarget.checked)
            }
          />,
        )}
      >
        {yesNo(businessInfo?.appts_need_confirmation)}
      </SettingsField>

      {/* New client / Any Available badges: None / Badge (icon chip) /
          Badge + Label (icon + short word where the event has room). */}
      {BADGE_DISPLAY_FIELDS.map(({ key, label, info }) => (
        <SettingsField
          key={key}
          label={label}
          info={info}
          editing={editing}
          editControl={
            <Select
              aria-label={label}
              data={BADGE_DISPLAY_MODE_OPTIONS}
              value={values[key]}
              onChange={(v) => v && setField(key, v)}
              allowDeselect={false}
              w="100%"
            />
          }
        >
          {badgeDisplayModeLabel(badgeSettingsFrom(businessInfo)[key])}
        </SettingsField>
      ))}

      {/* Calendar badges: which optional "Need to know" badges show on
          calendar events and in the appointment drawer. Requested,
          confirmed, not confirmed, arrived and forms missing always show. */}
      {BADGE_SETTING_FIELDS.map(({ key, label, info }, i) => (
        <SettingsField
          key={key}
          label={label}
          info={info}
          editing={editing}
          withDivider={i < BADGE_SETTING_FIELDS.length - 1}
          editControl={centerSwitch(
            <Switch
              aria-label={label}
              checked={values[key]}
              onChange={(e) => setField(key, e.currentTarget.checked)}
            />,
          )}
        >
          {badgeSettingsFrom(businessInfo)[key] ? "Shown" : "Hidden"}
        </SettingsField>
      ))}
    </StudioTabShell>
  );
}
